import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import ImageField from "./ImageField";
import { artists } from "../../mock";
import { splitEmails } from "../../lib/forms";

/* Per-block-type field editors. Each receives { block, set } where set(key, value) patches one field. */

export const uid = () => (typeof crypto?.randomUUID === "function" ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

const inputCls = "h-10 rounded-none border-border bg-transparent px-3 text-sm focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0";
const areaCls = "min-h-[90px] rounded-none border-border bg-transparent px-3 py-2 text-sm focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0";

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{label}</Label>
    {children}
  </div>
);

/** ISO string -> value for <input type="datetime-local"> in the browser's local time. */
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const HeadingBody = ({ block, set, bodyLabel = "Body" }) => (
  <>
    <Field label="Heading">
      <Input value={block.heading || ""} onChange={(e) => set("heading", e.target.value)} className={inputCls} data-testid={`block-heading-${block.id}`} />
    </Field>
    <Field label={bodyLabel}>
      <Textarea value={block.body || ""} onChange={(e) => set("body", e.target.value)} className={areaCls} data-testid={`block-body-${block.id}`} />
    </Field>
  </>
);

const CtaFields = ({ block, set }) => (
  <div className="grid gap-3 sm:grid-cols-2">
    <Field label="Button label">
      <Input value={block.cta_label || ""} onChange={(e) => set("cta_label", e.target.value)} className={inputCls} />
    </Field>
    <Field label="Button link">
      <Input value={block.cta_url || ""} onChange={(e) => set("cta_url", e.target.value)} placeholder="/artists or https://…" className={inputCls} />
    </Field>
  </div>
);

const TextFields = (props) => (
  <div className="space-y-3">
    <HeadingBody {...props} />
  </div>
);

const OfferFields = ({ block, set }) => (
  <div className="space-y-3">
    <Field label="Badge">
      <Input value={block.badge || ""} onChange={(e) => set("badge", e.target.value)} className={inputCls} />
    </Field>
    <HeadingBody block={block} set={set} />
    <CtaFields block={block} set={set} />
    <Field label="Offer ends (optional countdown)">
      <Input
        type="datetime-local"
        value={toLocalInput(block.expires_at)}
        onChange={(e) => set("expires_at", e.target.value ? new Date(e.target.value).toISOString() : null)}
        className={inputCls}
      />
    </Field>
  </div>
);

const ArtistsFields = ({ block, set }) => {
  const selected = block.artist_ids || [];
  const toggle = (id, checked) => set("artist_ids", checked ? [...selected, id] : selected.filter((x) => x !== id));
  return (
    <div className="space-y-3">
      <HeadingBody block={block} set={set} />
      <Field label={`Artists to feature (${selected.length} selected — none = first 6)`}>
        <div className="grid gap-2 sm:grid-cols-2">
          {artists.map((a) => (
            <label key={a.id} className="flex cursor-pointer items-center gap-2 border border-border px-3 py-2 text-sm hover:border-foreground/40">
              <Checkbox
                checked={selected.includes(a.id)}
                onCheckedChange={(v) => toggle(a.id, Boolean(v))}
                className="rounded-none data-[state=checked]:border-primary data-[state=checked]:bg-primary"
                data-testid={`block-artist-${a.id}`}
              />
              <span className="truncate">{a.name}</span>
            </label>
          ))}
        </div>
      </Field>
    </div>
  );
};

const FeaturesFields = ({ block, set }) => {
  const items = block.items || [];
  const updateItem = (id, patch) => set("items", items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  const removeItem = (id) => set("items", items.filter((it) => it.id !== id));
  const addItem = () => set("items", [...items, { id: uid(), title: "", body: "" }]);
  return (
    <div className="space-y-3">
      <HeadingBody block={block} set={set} />
      <Field label="Items">
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id} className="grid gap-2 border border-border p-3 sm:grid-cols-[1fr_2fr_auto]">
              <Input value={it.title || ""} placeholder="Title" onChange={(e) => updateItem(it.id, { title: e.target.value })} className={inputCls} />
              <Input value={it.body || ""} placeholder="Short description" onChange={(e) => updateItem(it.id, { body: e.target.value })} className={inputCls} />
              <Button type="button" size="icon" variant="ghost" onClick={() => removeItem(it.id)} aria-label="Remove item">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" className="rounded-none" onClick={addItem}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Add item
          </Button>
        </div>
      </Field>
    </div>
  );
};

const ImageFields = ({ block, set }) => (
  <div className="space-y-3">
    <ImageField label="Image" value={block.image_url || ""} onChange={(v) => set("image_url", v)} testId={`block-image-${block.id}`} />
    <HeadingBody block={block} set={set} bodyLabel="Caption" />
  </div>
);

const CtaBlockFields = ({ block, set }) => (
  <div className="space-y-3">
    <HeadingBody block={block} set={set} />
    <CtaFields block={block} set={set} />
  </div>
);

const FormFields = ({ block, set }) => (
  <div className="space-y-3">
    <HeadingBody block={block} set={set} bodyLabel="Intro text" />
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="Submit button label">
        <Input value={block.cta_label || ""} onChange={(e) => set("cta_label", e.target.value)} placeholder="Send request" className={inputCls} data-testid={`block-form-cta-${block.id}`} />
      </Field>
      <Field label="Also notify (comma separated emails)">
        <Input
          value={(block.recipients || []).join(", ")}
          onChange={(e) => set("recipients", splitEmails(e.target.value))}
          placeholder="Adds to the Forms & Email recipients"
          className={inputCls}
          data-testid={`block-form-recipients-${block.id}`}
        />
      </Field>
    </div>
    <Field label="Success message">
      <Textarea value={block.success_message || ""} onChange={(e) => set("success_message", e.target.value)} className={areaCls} placeholder="Thanks — we'll be in touch shortly." data-testid={`block-form-success-${block.id}`} />
    </Field>
    <div className="flex flex-wrap gap-4">
      <label className="flex cursor-pointer items-center gap-2 border border-border px-3 py-2 text-sm">
        <Checkbox checked={block.show_phone !== false} onCheckedChange={(v) => set("show_phone", Boolean(v))} className="rounded-none data-[state=checked]:border-primary data-[state=checked]:bg-primary" data-testid={`block-form-phone-${block.id}`} />
        Ask for phone
      </label>
      <label className="flex cursor-pointer items-center gap-2 border border-border px-3 py-2 text-sm">
        <Checkbox checked={block.show_message !== false} onCheckedChange={(v) => set("show_message", Boolean(v))} className="rounded-none data-[state=checked]:border-primary data-[state=checked]:bg-primary" data-testid={`block-form-message-${block.id}`} />
        Ask for a message
      </label>
    </div>
    <p className="text-xs text-muted-foreground">Submissions appear under Submissions → Promo lead and follow the "Promo lead" routing in Forms &amp; Email.</p>
  </div>
);

export const FIELD_EDITORS = {
  text: TextFields,
  offer: OfferFields,
  artists: ArtistsFields,
  features: FeaturesFields,
  image: ImageFields,
  cta: CtaBlockFields,
  form: FormFields,
};

/** Ensures every feature item has a stable id (older pages stored items without one). */
export const withItemIds = (block) =>
  block.type === "features" && Array.isArray(block.items) && block.items.some((it) => !it.id)
    ? { ...block, items: block.items.map((it) => (it.id ? it : { ...it, id: uid() })) }
    : block;

const BlockFields = ({ block, onChange }) => {
  const Editor = FIELD_EDITORS[block.type] || TextFields;
  const set = (key, value) => onChange({ ...block, [key]: value });
  return <Editor block={block} set={set} />;
};

export default BlockFields;
